/**
 * Focus Mode Utilities
 * 
 * Pure computation functions for focus mode calculations.
 * Extracted from useFocusMode for maintainability.
 * 
 * FOKUS MODE LOGIC:
 * When you focus on task(s), you see:
 * 1. The selected/filtered task(s) - the "target"
 * 2. ALL incomplete upstream dependencies recursively, INCLUDING the first
 *    completed task(s) at the boundary as visual anchors (but not beyond them)
 * 3. +1 downstream (immediate children only, shown faded)
 * 
 * This answers: "What do I need to complete to work on X?"
 */

import { isStatusCompleted } from '../../utils/statusUtils';
import { getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Get task ID from a dependency (handles both string and object format)
 * @param {string|Object} dep - Dependency (string ID or { taskId, branch })
 * @returns {string|null} Task ID
 */
export function getDependencyTaskId(dep) {
  if (typeof dep === 'string') return dep;
  if (dep && typeof dep === 'object') return dep.taskId;
  return null;
}

/**
 * Get branch from a dependency
 * @param {string|Object} dep - Dependency
 * @returns {string|null} Branch name or null
 */
export function getDependencyBranch(dep) {
  if (dep && typeof dep === 'object') return dep.branch || null;
  return null;
}

/**
 * Get all dependency task IDs for a task
 * Uses unlockConditions if present (v1 or v2), otherwise falls back to dependsOn
 * @param {Object} task - Task object
 * @returns {Array} Array of task ID strings
 */
function getEffectiveDependencies(task, allTasks) {
  const ids = getAllConditionTaskIds(task.unlockConditions, allTasks);
  if (ids.length > 0) return ids;
  return Array.isArray(task.dependsOn) ? task.dependsOn : [];
}

/**
 * Build a map of decision answers for quick lookup
 * @param {Array} tasks - All tasks
 * @returns {Map<string, string>} Map of taskId -> answer
 */
export function buildDecisionAnswersMap(tasks) {
  const answers = new Map();
  tasks.forEach(task => {
    if (task.nodeType === 'decision' && task.answer) {
      answers.set(task.id, task.answer);
    }
  });
  return answers;
}

/**
 * Calculate all focused task IDs with FOKUS logic:
 * - ALL incomplete upstream dependencies recursively, INCLUDING the first
 *   completed task(s) at the boundary (but not beyond them)
 * - +1 downstream (immediate children only)
 * 
 * Also tracks which tasks have hidden connections above/below
 * 
 * @param {Object} params
 * @param {Set} params.focusedTaskIds - Primary focused task IDs (the "target")
 * @param {Array} params.tasks - All tasks
 * @param {Map} params.decisionAnswers - Map of decision answers
 * @param {Array} params.statuses - Status configurations (for completion check)
 * @returns {{ allFocusedTaskIds: Set|null, hiddenConnections: Object, primaryFocusedIds: Set|null }}
 */
export function calculateAllFocusedTaskIds({ focusedTaskIds, tasks, decisionAnswers, statuses = [] }) {
  // null means not in focus mode at all
  if (!focusedTaskIds) {
    return { allFocusedTaskIds: null, hiddenConnections: {}, primaryFocusedIds: null };
  }
  
  // Empty set means in focus mode but no matches - return empty set (not null)
  if (focusedTaskIds.size === 0) {
    return { allFocusedTaskIds: new Set(), hiddenConnections: {}, primaryFocusedIds: focusedTaskIds };
  }
  
  const relatedIds = new Set(focusedTaskIds);
  const hidden = {};
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  
  /**
   * Recursively collect ALL incomplete upstream dependencies
   * Includes the first completed task(s) at the boundary as anchors,
   * but does not recurse past them.
   */
  const collectUpstreamRecursive = (taskId, visited = new Set()) => {
    if (visited.has(taskId)) return; // Prevent cycles
    visited.add(taskId);
    
    const task = taskMap.get(taskId);
    if (!task) return;
    
    const upstreamDeps = getEffectiveDependencies(task, tasks);
    
    upstreamDeps.forEach(dep => {
      const depId = getDependencyTaskId(dep);
      if (!depId) return;
      
      const depTask = taskMap.get(depId);
      if (!depTask) return;
      
      // Always include this upstream task
      relatedIds.add(depId);
      
      // Check if this upstream task is completed
      const isCompleted = isStatusCompleted(depTask.status, statuses);
      
      if (isCompleted) {
        // Include the completed task as a visual anchor, but stop here
        // (Don't recurse past completed tasks)
        return;
      }
      
      // Recursively check this task's upstream dependencies
      collectUpstreamRecursive(depId, visited);
    });
  };
  
  // For each focused task, collect ALL upstream and ONE level downstream
  focusedTaskIds.forEach(focusedTaskId => {
    const focusedTask = taskMap.get(focusedTaskId);
    if (!focusedTask) return;
    
    // ═══════════════════════════════════════════════════════════════
    // UPSTREAM: Recursively add ALL incomplete dependencies
    // ═══════════════════════════════════════════════════════════════
    collectUpstreamRecursive(focusedTaskId);
    
    // ═══════════════════════════════════════════════════════════════
    // DOWNSTREAM: Add ONE level only (immediate children)
    // ═══════════════════════════════════════════════════════════════
    // For decision nodes: if an answer is selected, only show that branch's downstream
    const isDecisionNode = focusedTask.nodeType === 'decision';
    const selectedAnswer = decisionAnswers.get(focusedTaskId);
    
    tasks.forEach(task => {
      const taskDeps = getEffectiveDependencies(task, tasks);
      
      // Check if this task depends on the focused task
      const matchingDep = taskDeps.find(dep => getDependencyTaskId(dep) === focusedTaskId);
      
      if (matchingDep) {
        // For decision nodes with a selected answer, only include matching branch
        if (isDecisionNode && selectedAnswer) {
          const depBranch = getDependencyBranch(matchingDep);
          if (depBranch && depBranch !== selectedAnswer) {
            return; // Skip this downstream task
          }
        }
        
        relatedIds.add(task.id);
        
        // Check if this downstream task has MORE dependents (hidden below)
        const hasHiddenBelow = tasks.some(t => {
          const tDeps = getEffectiveDependencies(t, tasks);
          return tDeps.some(d => getDependencyTaskId(d) === task.id) && 
            !focusedTaskIds.has(t.id) && 
            !relatedIds.has(t.id);
        });
        if (hasHiddenBelow) {
          hidden[task.id] = { ...hidden[task.id], below: true };
        }
      }
    });
  });
  
  // Mark hidden connections for tasks at the boundary
  relatedIds.forEach(taskId => {
    const task = taskMap.get(taskId);
    if (!task) return;
    
    // Completed boundary tasks: mark "hidden above" if they have further upstream deps
    // (since we stop recursing past completed tasks, anything above them is hidden)
    if (isStatusCompleted(task.status, statuses)) {
      const hasUpstreamBeyond = getEffectiveDependencies(task, tasks).some(dep => {
        const depId = getDependencyTaskId(dep);
        if (!depId) return false;
        const depTask = taskMap.get(depId);
        if (!depTask) return false;
        // Hidden if there are upstream tasks we didn't include
        return !relatedIds.has(depId);
      });
      
      if (hasUpstreamBeyond) {
        hidden[taskId] = { ...hidden[taskId], above: true };
      }
    }
  });
  
  tasks.forEach((task) => {
    if (task.nodeType !== 'note') return;
    const hostTaskId = task.noteAnchor?.hostTaskId;
    if (hostTaskId && relatedIds.has(hostTaskId)) {
      relatedIds.add(task.id);
    }
  });

  return { 
    allFocusedTaskIds: relatedIds, 
    hiddenConnections: hidden,
    primaryFocusedIds: focusedTaskIds,
  };
}

/**
 * Filter tasks for layout, only including dependencies within the focused set
 * Also filters out edges from decision nodes to non-selected branches
 * 
 * @param {Object} params
 * @param {Array} params.displayTasks - Tasks to display
 * @param {Set} params.allFocusedTaskIds - All focused task IDs
 * @param {Array} params.tasks - All tasks (for decision node lookup)
 * @param {Map} params.decisionAnswers - Map of decision answers
 * @returns {Array|null} Filtered tasks for layout
 */
export function filterTasksForLayout({ displayTasks, allFocusedTaskIds, tasks, decisionAnswers }) {
  if (!allFocusedTaskIds) return null;
  
  return displayTasks.map(task => ({
    ...task,
    // Filter dependencies to only show those in the focused set
    dependsOn: getEffectiveDependencies(task, tasks).filter(dep => {
      const depTaskId = getDependencyTaskId(dep);
      
      // Must be in the focused set
      if (!depTaskId || !allFocusedTaskIds.has(depTaskId)) return false;
      
      // If this dependency is from a decision node with a selected answer,
      // only include if the branch matches
      const depTask = tasks.find(t => t.id === depTaskId);
      if (depTask?.nodeType === 'decision') {
        const selectedAnswer = decisionAnswers.get(depTaskId);
        const depBranch = getDependencyBranch(dep);
        if (selectedAnswer && depBranch && depBranch !== selectedAnswer) {
          return false;
        }
      }
      
      return true;
    }),
  }));
}

/**
 * Filter edges for display, respecting decision branch selection
 * 
 * @param {Object} params
 * @param {Array} params.focusEdges - Focus mode edges (if available)
 * @param {Array} params.edges - All edges
 * @param {Set} params.allFocusedTaskIds - All focused task IDs
 * @param {Map} params.decisionAnswers - Map of decision answers
 * @param {boolean} params.hasFocusEdges - Whether focus edges are available
 * @returns {Array} Filtered edges
 */
export function filterEdgesForDisplay({ focusEdges, edges, allFocusedTaskIds, decisionAnswers, hasFocusEdges }) {
  if (hasFocusEdges && focusEdges) {
    return focusEdges;
  }
  
  if (!allFocusedTaskIds) return edges || [];
  
  // If allFocusedTaskIds is empty (no matches), return empty array
  if (allFocusedTaskIds.size === 0) return [];
  
  return (edges || []).filter(e => {
    // Must be in the focused set
    if (!allFocusedTaskIds.has(e.from) || !allFocusedTaskIds.has(e.to)) return false;
    
    // If this edge is from a decision node with a branch, filter by selected answer
    if (e.isFromDecision && e.branch) {
      const selectedAnswer = decisionAnswers.get(e.from);
      if (selectedAnswer && e.branch !== selectedAnswer) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Calculate bounds from positions
 * 
 * @param {Object} positions - Position map { taskId: { x, y } }
 * @param {Object} nodeDimensions - Dimension map { taskId: { width, height } }
 * @param {number} padding - Padding around bounds
 * @returns {Object|null} Bounds { minX, minY, maxX, maxY, width, height }
 */
export function calculateBoundsFromPositions(positions, nodeDimensions, padding = 800) {
  const positionValues = Object.values(positions);
  if (positionValues.length === 0) return null;
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  Object.entries(positions).forEach(([taskId, pos]) => {
    const dims = nodeDimensions[taskId] || { width: 290, height: 150 };
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + dims.width);
    maxY = Math.max(maxY, pos.y + dims.height);
  });
  
  return {
    minX: minX - padding,
    minY: minY - padding,
    maxX: maxX + padding,
    maxY: maxY + padding,
    width: (maxX - minX) + (padding * 2),
    height: (maxY - minY) + (padding * 2),
  };
}

/**
 * Calculate bounds for primary focused tasks only
 * Returns bounds and topmost Y position for alignment
 * 
 * @param {Object} params
 * @param {Object} params.focusAutoPositions - Auto-layout positions
 * @param {Set} params.focusedTaskIds - Primary focused task IDs
 * @param {Object} params.nodeDimensions - Node dimensions
 * @returns {{ minX, minY, maxX, maxY, topmostY, contentWidth }|null}
 */
export function calculatePrimaryFocusBounds({ focusAutoPositions, focusedTaskIds, nodeDimensions }) {
  if (!focusAutoPositions) return null;
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let topmostY = Infinity;
  
  // Use primary focused tasks if available, otherwise all
  const taskIds = focusedTaskIds && focusedTaskIds.size > 0 
    ? focusedTaskIds 
    : new Set(Object.keys(focusAutoPositions));
  
  taskIds.forEach(taskId => {
    const pos = focusAutoPositions[taskId];
    if (!pos) return;
    const dims = nodeDimensions[taskId] || { width: 290, height: 150 };
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x + dims.width);
    maxY = Math.max(maxY, pos.y + dims.height);
    topmostY = Math.min(topmostY, pos.y);
  });
  
  if (minX === Infinity) return null;
  
  return {
    minX,
    minY,
    maxX,
    maxY,
    topmostY,
    contentWidth: maxX - minX,
  };
}

